import type { ErrorEvent, EventHint } from '@sentry/react';

import { isApiError, isManagementClientAuthError, isTimeoutError } from '@keboola/api-client';

/** What a 5xx response contributes to an event's fingerprint. */
export type ServerErrorMeta = {
  method: string;
  url: string | undefined;
  status: number;
};

/**
 * Hooks an app adds to the shared rules.
 *
 * - `isExpected` runs before the shared drop rules; `true` drops the event.
 * - `serverErrorMeta` runs before the api-client extractor, for a non-api-client HTTP layer.
 */
export type BeforeSendHooks = {
  isExpected?: (exception: unknown) => boolean;
  serverErrorMeta?: (exception: unknown) => ServerErrorMeta | null;
};

/**
 * A pathname with the query string and fragment dropped.
 *
 * - Query parameters carry PII and make every request its own Sentry issue.
 * - The fragment carries single-use tokens (`/reset-password#token=…`).
 * - Anything but a non-empty string returns `''`.
 */
export const safePathname = (url: unknown): string => {
  if (typeof url !== 'string' || !url) return '';

  try {
    return new URL(url).pathname;
  } catch {
    return url.split(/[?#]/, 1)[0] ?? '';
  }
};

/** A job or file id. */
const NUMERIC_SEGMENT = /^\d+$/;
/** A Keboola table id, `in.c-main.users`. Two dots, so `v1.0` and `export.csv` are not ids. */
const TABLE_ID_SEGMENT = /\..*\./;
/** A Keboola bucket id, `in.c-main`. The letter after the dot keeps `in.2` out. */
const BUCKET_ID_SEGMENT = /^(in|out)\.[a-zA-Z]/;

/** A pathname with resource-id segments replaced by `{id}`, so one endpoint is one issue. */
export const normalizePath = (pathname: string): string =>
  pathname
    .split('/')
    .map((segment) => {
      if (!segment) return segment;

      const isId =
        NUMERIC_SEGMENT.test(segment) ||
        TABLE_ID_SEGMENT.test(segment) ||
        BUCKET_ID_SEGMENT.test(segment);

      return isId ? '{id}' : segment;
    })
    .join('/');

/** The messages a browser rejects `fetch` with when the request never reached a server. */
const TRANSPORT_FAILURE_MESSAGES = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
];

/**
 * A request that never got an HTTP response (offline, DNS, dead host, `AbortSignal.timeout`).
 *
 * - Network noise, not a defect; one backgrounded tab can report thousands.
 * - A timeout matches on `DOMException.name`; an `AbortError` still reports.
 * - Gap: `xhrFetch` aborts without `signal.reason`, so its timeouts are kept.
 * - Relies on `enhanceFetchErrorMessages: 'report-only'` leaving the message unchanged.
 */
const isTransportFailure = (exception: unknown): boolean =>
  isTimeoutError(exception) ||
  (exception instanceof TypeError && TRANSPORT_FAILURE_MESSAGES.includes(exception.message));

/** A 5xx as api-client throws it. */
const apiClientServerErrorMeta = (exception: unknown): ServerErrorMeta | null => {
  if (!isApiError(exception) || exception.response.status < 500) return null;

  return {
    method: exception.request.method || '',
    url: exception.request.url,
    status: exception.response.status,
  };
};

/**
 * Fingerprints a 5xx by method, normalized endpoint and status, and retitles it to match.
 *
 * - No `{{ default }}`: minified stack traces differ per build, splitting issues per deploy.
 * - Only the event title changes, not the thrown error's message.
 * - See: https://docs.sentry.io/platforms/javascript/enriching-events/fingerprinting/
 */
const fingerprintServerError = (event: ErrorEvent, meta: ServerErrorMeta): void => {
  const pathname = normalizePath(safePathname(meta.url));
  const status = String(meta.status || '');
  const { method } = meta;
  const parts = [method, pathname, status].filter(Boolean);

  if (!parts.length) return;

  event.fingerprint = ['server-error', ...parts];

  const value = event.exception?.values?.[0];

  if (value) {
    const title = [method, pathname, status && `(${status})`].filter(Boolean).join(' ');
    value.value = `Server error: ${title}`;
  }
};

/** A URL with its query string and fragment dropped. */
const withoutQuery = (url: string): string => url.split(/[?#]/, 1)[0] ?? '';

/**
 * Decides what reaches Sentry, and how server errors group there.
 *
 * - `null` drops an expected, already-handled condition.
 * - The app's `isExpected` runs before the shared rules.
 * - `request.url` is `location.href` at capture time; single-use credentials (invite and
 *   password-reset tokens) arrive in its query string or fragment, so both go.
 */
export const createBeforeSend =
  ({ isExpected, serverErrorMeta }: BeforeSendHooks = {}) =>
  (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    const exception = hint.originalException;

    // An expired management session repeats per request and per page; the app signs the user out.
    if (isExpected?.(exception) || isManagementClientAuthError(exception)) return null;
    if (isTransportFailure(exception)) return null;

    const meta = serverErrorMeta?.(exception) ?? apiClientServerErrorMeta(exception);
    if (meta) fingerprintServerError(event, meta);

    if (event.request?.url) event.request.url = withoutQuery(event.request.url);

    return event;
  };
